import React, { useEffect, useState } from "react"
import { useSearch } from "@tanstack/react-router"
import { Code2, KeyRound } from "lucide-react"

import { useAuth } from "../lib/auth-context"
import { getSafeAuthRedirect } from "../lib/authRedirect"
import { isPasskeyEnabled, supabase } from "../lib/supabase"

export default function AuthScreen() {
  const searchParams = useSearch({ from: "/auth" })

  const [loading, setLoading] = useState(false)
  // Which provider is mid-flight, so the spinner shows on the clicked button.
  const [activeProvider, setActiveProvider] = useState<"google" | "passkey" | "dev" | null>(null)
  const [error, setError] = useState("")
  const { signInWithGoogle, devLogin, isDevLoginEnabled, user } = useAuth()

  // Redirect after successful authentication
  useEffect(() => {
    if (user) {
      window.location.replace(getSafeAuthRedirect(searchParams.redirect))
    }
  }, [user, searchParams.redirect])

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setActiveProvider("google")
    setError("")

    try {
      // Store redirect path for after OAuth callback
      const redirectTo = getSafeAuthRedirect(searchParams.redirect)
      localStorage.setItem("authRedirect", redirectTo)

      const result = await signInWithGoogle(redirectTo)
      if (result.error) {
        setError(result.error)
        localStorage.removeItem("authRedirect")
      }
    } catch {
      setError("Failed to sign in with Google")
      localStorage.removeItem("authRedirect")
    } finally {
      setLoading(false)
      setActiveProvider(null)
    }
  }

  const handlePasskeySignIn = async () => {
    setLoading(true)
    setActiveProvider("passkey")
    setError("")
    try {
      const { error: signInError } = await supabase.auth.signInWithPasskey()
      if (signInError) setError(signInError.message)
    } catch (signInError) {
      setError(signInError instanceof Error ? signInError.message : "Passkey sign-in failed")
    } finally {
      setLoading(false)
      setActiveProvider(null)
    }
  }

  // Local-dev only: one-click login that bypasses Google OAuth. The redirect is
  // handled by the `user` effect above once the dev session hydrates.
  const handleDevLogin = async () => {
    setLoading(true)
    setActiveProvider("dev")
    setError("")
    try {
      const result = await devLogin()
      if (result.error) {
        setError(result.error)
      }
    } catch {
      setError("Dev login failed")
    } finally {
      setLoading(false)
      setActiveProvider(null)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">Sign in to your account</h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            {isPasskeyEnabled
              ? "Use your Google account or a saved passkey to continue"
              : "Use your Google account to continue"}
          </p>
        </div>

        {/* Email/Password form commented out - keeping only Google sign-in */}
        {/* <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            {mode === 'signup' && (
              <div>
                <label htmlFor="name" className="sr-only">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500  rounded-t-md focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500  ${
                  mode === 'signup' ? '' : 'rounded-t-md'
                } focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {mode !== 'reset' && (
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500  rounded-b-md focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
          </div>

          {mode === 'signin' && (
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setMode('reset')}
                  className="font-medium  hover:text-blue-500"
                >
                  Forgot your password?
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-destructive">{error}</div>
            </div>
          )}

          {message && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-700">{message}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {mode === 'signin' && 'Sign in'}
              {mode === 'signup' && 'Sign up'}
              {mode === 'reset' && 'Send reset email'}
            </button>
          </div>

          {mode !== 'reset' && (
            <div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-foregroundx text-gray-500">Or continue with</span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full inline-flex justify-center py-2 px-4 border border-border rounded-md shadow-sm bg-card text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="ml-2">Google</span>
                </button>
              </div>
            </div>
          )}
        </form> */}

        {/* Sign-in methods */}
        <div className="mt-8 space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-destructive">{error}</div>
            </div>
          )}

          <div>
            {isPasskeyEnabled && (
              <>
                <button
                  type="button"
                  onClick={() => void handlePasskeySignIn()}
                  disabled={loading || !window.isSecureContext || typeof PublicKeyCredential === "undefined"}
                  className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <KeyRound size={18} />
                  {activeProvider === "passkey" ? "Signing in..." : "Sign in with passkey"}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="inline-flex w-full justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {activeProvider === "google" ? (
                <svg
                  className="-ml-1 mr-3 h-5 w-5 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              <span className="ml-2">Sign in with Google</span>
            </button>
          </div>

          {/* Local-dev only: one-click login, bypasses Google OAuth. Tree-shaken out of prod builds. */}
          {isDevLoginEnabled && (
            <div className="pt-2">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-2 text-muted-foreground">Local dev</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDevLogin}
                disabled={loading}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md border border-orange-500/40 px-4 py-2 text-sm font-medium text-orange-500 hover:bg-orange-500/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {activeProvider === "dev" ? (
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <Code2 size={16} />
                )}
                Dev login (shray@aixlabs.co)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
